import { useContext, useMemo } from 'react';
import { CountryCode } from '@dayetopia/library/types';
import { useLocation } from '@reach/router';
import { RichTextBlock } from 'prismic-reactjs';
import { Context } from '@components/Context';
import { FAQData, ProcessedFAQData } from '@contracts';
import { filterShowInCountries } from './country';
import { replaceText } from './handleTextReplacement';
import { pageIsAdsPage } from './helpers';

const HIDDEN_PATHS = [
  '/products/organic-tampons/',
  '/products/vaginal-health-proviotics/',
  '/festive-gifts/',
  '/period-pain-clinic/',
  '/products/organic-coated-tampons/',
  '/1/' // usMetaHomeURL
];

const CBD_QUESTIONS = new Set([
  'How do CBD tampons work?',
  "Can I use a CBD tampon if I'm under the age of 18?",
  'Do they help with UTIs?',
  'Do they have side effects?',
  'Will they cure thrush?',
  'How long do I have to take them for?',
  'I can’t afford to buy these every month, would taking them every few days be beneficial too?',
  'How do I take these vaginally?',
  'Is it more effective to take them orally or vaginally?',
  'Do the pads have CBD on them?',
  'What lab do you process my results in? Is it UKAS accredited?'
]);

const HIDE_QUESTIONS_US = new Set([
  "What's the difference between a monthly and a multi-month subscription?",
  'How can I claim the remaining kits from my subscription?'
]);

function useProcessedFaqs(faqItems: FAQData[]): ProcessedFAQData[] {
  const {
    state: { country }
  } = useContext(Context);
  const location = useLocation();
  const processedFaqItems: ProcessedFAQData[] = useMemo(() => {
    const processedArray: ProcessedFAQData[] = [];

    if (!faqItems?.length || !faqItems[0]?.question?.text) {
      return processedArray;
    }

    const countryFaqItems = filterShowInCountries<FAQData>(faqItems, country);

    countryFaqItems.forEach((faqItem) => {
      const {
        question: { text: questionText },
        answer: { richText: answerText, text: answerTextString }
      } = faqItem;

      if (shouldSkipQuestion(questionText, country, location)) {
        return;
      }

      const modifiedQuestionText = replaceText(questionText, country, location);
      const replacementAnswerText = processAnswerText(answerText, country, location);
      const replacementTextString = replaceText(answerTextString, country, location);

      processedArray.push({
        questionText: modifiedQuestionText,
        answerText: replacementAnswerText,
        answerTextString: replacementTextString
      });
    });

    return processedArray;
  }, [faqItems, country, location]);

  return processedFaqItems;
}

function shouldSkipQuestion(questionText: string, country: CountryCode, location: Location): boolean {
  const isPathIncluded = HIDDEN_PATHS.some((path) => location?.pathname.endsWith(path));
  const isPageForAds = pageIsAdsPage(location.pathname);
  const hideCBDWords = !location || isPathIncluded || isPageForAds;

  return (hideCBDWords && CBD_QUESTIONS.has(questionText)) || (country === CountryCode.US && HIDE_QUESTIONS_US.has(questionText));
}

function processAnswerText(answerText: RichTextBlock[], country: CountryCode, location: Location): RichTextBlock[] {
  return answerText.map((block) => ({
    ...block,
    text: replaceText(block.text, country, location)
  }));
}

export default useProcessedFaqs;
